import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import {
  useConsumeLocationState,
  useLocationState,
} from "../../app/hooks/useLocationState";
import { useActiveLang } from "../../app/hooks/useActiveLang";
import { dropTurnToRetry, useGptChat } from "../hooks/useGptChat";
import type { GptStreamFailure } from "../../../../../api/src/shared/gpt-stream-failure";
import { useTrackOnMount } from "../../../hooks/use-track-on-mount";
import {
  gaEventGptAnonymousLimitReached,
  gaEventGptAnonymousQuestionAsked,
  gaEventGptAnonymousSignupClicked,
  type AnonymousGptLimitKind,
} from "../../../tools/analytics/domains/gpt.analytics";
import {
  returnToJuisciGptAfterSignIn,
  useAnonymousAllowance,
} from "./anonymousGpt";

const handOffSchema = z.object({
  initialQuestion: z.string().nullable().default(null),
  initialQuestionOwnerUid: z.string().nullable().default(null),
});

/**
 * The logged-out JuisciGPT session behind `GPTAnonymousChatPage`: the chat,
 * today's allowance, how the question of this session ended, and the sign-in
 * door. The page renders it and calls its callbacks.
 *
 * Each question is a session of its own (`sessionKey`), and a question is spent
 * once the server has taken it: the server keeps generating, and settles,
 * whether the visitor stopped the turn or lost the connection. So a finished
 * turn, a stopped one and a disconnected one all lead to the offer, and another
 * question is a new session there, offered when the allowance read after it
 * says one is left.
 *
 * A question another surface handed over (`initialQuestion` location state,
 * e.g. from the public site's homepage, `questionHandoff.ts`) is asked once the
 * allowance answers, like a typed one. When the allowance refuses it (today's
 * questions used, or an account signed in on this browser) the page shows the
 * offer or the login, with no composer, and the question is dropped.
 */
export function useAnonymousGptSession(device: string | null) {
  const { activeLang } = useActiveLang();
  const allowanceQuery = useAnonymousAllowance(device);
  const [sessionKey, setSessionKey] = useState(() => crypto.randomUUID());
  const [draft, setDraft] = useState("");
  const history = useHistory();
  // How the question of THIS session failed, if it did. Three different facts:
  // a 429 is a ceiling; another refusal means the server never generated (the
  // reservation was released) and the question can be asked again; a dropped
  // connection means the server may still be answering, and settling, a
  // question the visitor will not see.
  const [failure, setFailure] = useState<GptStreamFailure | null>(null);
  // Today's rank of the question in flight, read when it is sent and reported
  // once the server accepts it. A ref: a callback consumes it, and nothing
  // renders from it.
  const askedRank = useRef<number | null>(null);

  // Null while the read is in flight or failed: nothing is promised then, and
  // the stream's own admission is the ceiling that holds. A refetch keeps the
  // previous answer on screen, so `isFetching` is what says it is stale.
  const allowance =
    allowanceQuery.state === "success" ? allowanceQuery.body : null;
  const refusal: AnonymousGptLimitKind | null =
    allowance !== null && allowance.state === "refused"
      ? allowance.reason
      : null;

  const chat = useGptChat({
    transport: { kind: "anonymous", sessionKey, language: activeLang, device },
    initialMessages: [],
    onTurnAccepted: () => {
      gaEventGptAnonymousQuestionAsked({ questionNumber: askedRank.current });
    },
    // The answer is in: what follows it depends on what is left today.
    onTurnFinish: () => {
      allowanceQuery.refetch();
    },
    // The visitor has no conversation page to resume on, so a dropped stream
    // has nothing to be recovered from here. A 429 is a ceiling: the
    // allowance names which.
    onFailure: (next) => {
      setFailure(next);
      allowanceQuery.refetch();
    },
  });

  // The question on screen: the LAST one asked, which a retry appends.
  const question = chat.messages.findLast((m) => m.role === "user");
  const isSpent =
    question !== undefined &&
    (chat.status === "ready" ||
      // A reported failure counts like a drop: once a step has finished, the
      // server settles the visitor's reservation rather than releasing it, so
      // the question may already be one of their two.
      (failure !== null &&
        (failure.kind === "disconnected" || failure.kind === "failed")));
  // Only a refusal that is not a ceiling can be retried: the server released
  // the reservation without generating, so asking again charges nothing twice.
  // A 403 is not one either: an account signed in on this browser, and the
  // allowance read after it puts the login on screen (`isMember`).
  const canRetry =
    failure !== null &&
    failure.kind === "rejected" &&
    failure.status !== 429 &&
    failure.status !== 403;
  const wasRefused =
    failure !== null && failure.kind === "rejected" && failure.status === 429;

  // A refusal is counted when a question met it: on the landing (the day was
  // already used) or on a 429. Not when the visitor used their last question.
  const refusalMet = question === undefined || wasRefused ? refusal : null;
  useTrackOnMount(
    "gpt-anonymous-refusal-shown",
    refusalMet === null
      ? { status: "hidden" }
      : { status: "shown", key: refusalMet },
    () => {
      if (refusalMet === null)
        throw new Error("should not happen: a hidden refusal was tracked");
      gaEventGptAnonymousLimitReached({ limitKind: refusalMet });
    }
  );

  // Consumed before it is acted on, as on the signed-in landing: a restore of
  // this entry (reload, browser reopen) must not ask it again. A ref, because
  // the hand-off happens once per arrival and nothing renders from it.
  const { initialQuestion: handedQuestion, initialQuestionOwnerUid } =
    useLocationState(handOffSchema);
  const consumeLocationState = useConsumeLocationState();
  const handOffDone = useRef(false);
  useEffect(() => {
    if (handedQuestion === null || handOffDone.current) return;
    // A question held for an authenticated account cannot become anonymous.
    if (initialQuestionOwnerUid !== null) {
      handOffDone.current = true;
      consumeLocationState();
      return;
    }
    if (allowanceQuery.state === "pending") return;
    handOffDone.current = true;
    consumeLocationState();
    if (refusal !== null) return;
    if (allowance !== null && allowance.state === "member") return;
    ask(handedQuestion);
  }, [allowanceQuery.state]);

  function ask(text: string): void {
    setDraft("");
    askedRank.current =
      allowance === null || allowance.state === "member"
        ? null
        : allowance.questionsAsked + 1;
    void chat.sendMessage({ text });
  }

  function askAnother(): void {
    setFailure(null);
    setSessionKey(crypto.randomUUID());
  }

  function retry(): void {
    const toRetry = dropTurnToRetry(chat.messages);
    if (toRetry === null) return;
    setFailure(null);
    chat.setMessages(toRetry.kept);
    ask(toRetry.question);
  }

  // "Log in" opens the desktop login door, the offer the signup door. Either
  // door answers both audiences (`check-email` decides). The sign-in comes
  // back to JuisciGPT, which opens the conversation on screen, claimed into
  // the account.
  function openDoor(pathname: "/login" | "/signup"): void {
    returnToJuisciGptAfterSignIn();
    history.push({ pathname, state: { surface: "anonymous_gpt_wall" } });
  }

  function openLogin(): void {
    openDoor("/login");
  }

  function openSignup(): void {
    gaEventGptAnonymousSignupClicked({ limitKind: refusal });
    openDoor("/signup");
  }

  return {
    chat,
    sessionKey,
    draft,
    setDraft,
    question,
    refusal,
    // An account has signed in on this browser: its owner logs in, no question.
    isMember: allowance !== null && allowance.state === "member",
    // Nothing is offered before the allowance answers, so a member's browser
    // never shows a composer it would refuse. A failed read offers it: the
    // stream's own admission is then the rule.
    isAllowancePending: allowanceQuery.state === "pending",
    isSpent,
    canRetry,
    // Another question is offered once the allowance read after the answer
    // says one is left, or when it cannot be read at all.
    canAskAnother:
      !allowanceQuery.isFetching &&
      (allowanceQuery.state === "error" ||
        (allowance !== null && allowance.state === "open")),
    ask,
    askAnother,
    retry,
    openLogin,
    openSignup,
  };
}
